import {
  Box,
  Typography,
  Button,
  Snackbar,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { usePendingPlan } from "../hooks/logic/usePendingPlan";
import { isNotGeneralUser } from "../constants/roles";
import { PendingPlanStyle as styles } from "./PendingPlanStyle";
import SearchByGenre from "../components/SearchByGenre";
import UserFilterSelect from "../components/UserFilter";
import ProductInfoDialog from "../components/ProductInfoDialog";
import PendingPlanTable from "../components/pendingPlan/PendingPlanTable";
import CreateInboundShipmentPlanDialog from "../components/CreateInboundShipmentPlanDialog";

const PendingPlan = () => {
  const {
    inputRef,
    loginUser,
    displayList,
    userOptions,
    selectedUserId,
    handleUserChange,
    checkedIds,
    handleCheckChange,
    searchGenres,
    selectedGenreId,
    setSelectedGenreId,
    focusInputField,
    handleGenreChange,
    searchKeyword,
    setSearchKeyword,
    searchItems,
    resetFilter,
    resetGenreWord,
    handleKeywordChange,
    handleRowClick,
    detailDialogOpen,
    setDetailDialogOpen,
    selectedParent,
    setSelectedParent,
    selectedChildren,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    resetCheckCode,
    fileInputRef,
    handleOpenFileDialog,
    handleFileChange,
    handleCreatePlan,
    handleRegisterShippingInfo,
    createPlanDialogOpen,
    setCreatePlanDialogOpen,
    handlePageChange,
    isLoading,
    getShippingSourceInformation,
    shippingInformation,
    toastCloseTime,
    totalListCount,
    handleDownloadManifestExcelByUser,
    changeSortKey,
  } = usePendingPlan();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <ProductInfoDialog
        open={detailDialogOpen}
        onClose={() => {
          setSelectedParent(null);
          selectedChildren.current = [];
          setDetailDialogOpen(false);
        }}
        title="商品情報"
        parentItem={selectedParent!}
        childItems={selectedChildren.current}
        statusId={selectedParent?.statusId ?? 0}
      />
      <CreateInboundShipmentPlanDialog
        open={createPlanDialogOpen}
        onClose={() => {
          // setSelectedParent(null);
          setCreatePlanDialogOpen(false);
        }}
        onCreatePlan={handleCreatePlan}
        // onCreatePlan={handleDownloadManifestExcelByUser}
        onRegisterShipppingInfo={handleRegisterShippingInfo}
        shippingInformation={shippingInformation}
      />
      <Box sx={styles.titleBar}>
        <Typography variant="h6" sx={styles.titleText}>
          プラン作成待ち
        </Typography>
      </Box>
      <Box py={0} px={0}>
        <Box
          display="flex"
          // gap={2}
          // mb={2}
          // flexWrap="wrap"
          // alignItems="center"
          px={1}
          justifyContent="space-between"
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
              {isNotGeneralUser(loginUser?.permissionId) && (
                <UserFilterSelect
                  options={userOptions}
                  onSelect={handleUserChange}
                  value={selectedUserId}
                  sx={{ width: "300px" }}
                />
              )}
              <Button
                variant="outlined"
                onClick={async () => {
                  await resetFilter();
                  resetCheckCode();
                }}
              >
                フィルター解除
              </Button>
              <Typography py={1}>総数：{}個</Typography>
            </Box>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <SearchByGenre
                options={["JAN", "ASIN", "商品名", "FNSKU", "SKU"]}
                defaultGenre="JAN"
                onGenreChange={handleGenreChange}
                searchKeyword={searchKeyword}
                onKeywordChange={setSearchKeyword}
                autoFocus={true}
                onEnter={focusInputField}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  searchItems(searchKeyword);
                }}
              >
                検索
              </Button>
            </Box>
          </Box>

          {isNotGeneralUser(loginUser?.permissionId) && (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-start"
              gap={4}
              mt={2}
              mb={2}
            >
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                marginRight={2}
              >
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleOpenFileDialog}
                >
                  ピッキングリストアップロード
                </Button>
                <Box display="flex" justifyContent="flex-start">
                  {checkedIds.size > 0 ? (
                    <Typography py={1}>
                      チェックが付いた{checkedIds.size}個の商品で　
                    </Typography>
                  ) : (
                    <Typography py={1}></Typography>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={checkedIds.size === 0}
                    onClick={() => getShippingSourceInformation(true)}
                  >
                    プラン作成
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={checkedIds.size === 0}
                    onClick={() => handleDownloadManifestExcelByUser()}
                    sx={{ marginLeft: 4 }}
                  >
                    Excelダウンロード
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box mt={2}>
          <PendingPlanTable
            data={displayList}
            totalListCount={totalListCount}
            checkedIds={checkedIds}
            onCheckedChange={handleCheckChange}
            onParentRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onSortChange={(key, ord) => {
              changeSortKey(key, ord);
            }}
          ></PendingPlanTable>
        </Box>
        <Backdrop
          open={isLoading}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1000,
            color: "#fff",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={toastCloseTime}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseToast}
          severity={toastSeverity}
          sx={{ width: "100%", whiteSpace: "pre-line" }}
          elevation={6}
          variant="filled"
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>
      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </LocalizationProvider>
  );
};

export default PendingPlan;
